import React, { useState } from "react";
import {
  ncbInterpolColors,
  getNCBClasses,
} from "../../../modules/shared/utilities/colorConfig";

interface TabConfig {
  id: string;
  label: string;
  icon: string;
  component: React.ReactNode;
}

interface ProfilePageTemplateProps {
  title: string;
  subtitle?: string;
  icon?: string;
  breadcrumbBase: string;
  tabs: TabConfig[];
  footerStats?: {
    label: string;
    value: string;
  }[];
  footerTitle?: string;
  footerDescription?: string;
}

const ProfilePageTemplate: React.FC<ProfilePageTemplateProps> = ({
  title,
  subtitle,
  icon = "🏢",
  breadcrumbBase,
  tabs,
  footerStats,
  footerTitle,
  footerDescription,
}) => {
  const [activeTab, setActiveTab] = useState(tabs[0]?.id || "");

  const renderTabContent = () => {
    const activeTabConfig = tabs.find((tab) => tab.id === activeTab);
    return activeTabConfig?.component || null;
  };

  return (
    <div className={getNCBClasses.pageContainer()}>
      <div className="mx-auto w-11/12 laptop:max-w-full desktop:max-w-7xl">
        <div className="py-8">
          {/* Header Section */}
          <div className={`${getNCBClasses.headerSection()} mb-8`}>
            <div className={getNCBClasses.headerGradient()}>
              <div className="flex flex-col laptop:flex-row justify-between items-start laptop:items-center">
                <div className="flex items-center gap-x-4 mb-4 laptop:mb-0">
                  <div
                    className={`w-16 h-16 ${ncbInterpolColors.header.iconBg} ${ncbInterpolColors.common.roundedFull} flex items-center justify-center`}
                  >
                    <span className="text-2xl">{icon}</span>
                  </div>
                  <div>
                    <h1
                      className={`text-3xl font-bold ${ncbInterpolColors.header.text}`}
                    >
                      {title}
                    </h1>
                    {subtitle && (
                      <p className={`${ncbInterpolColors.header.subtext} mt-1`}>
                        {subtitle}
                      </p>
                    )}
                  </div>
                </div>
                <nav
                  className={`text-sm ${ncbInterpolColors.header.subtext}`}
                  aria-label="Breadcrumb"
                >
                  <ol className="flex items-center gap-x-2">
                    <li>{breadcrumbBase}</li>
                    <li className="flex items-center gap-x-2">
                      <span>•</span>
                      <span
                        className={`${ncbInterpolColors.header.text} font-medium`}
                      >
                        {title}
                      </span>
                    </li>
                  </ol>
                </nav>
              </div>
            </div>

            {/* Tab Navigation */}
            {tabs.length > 1 && (
              <div
                className={`border-b ${ncbInterpolColors.background.border}`}
              >
                <div className="px-8">
                  <nav className="flex gap-x-8" aria-label="Tabs">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={getNCBClasses.tabButton(
                          activeTab === tab.id,
                        )}
                      >
                        <span className="flex items-center gap-x-2">
                          <span>{tab.icon}</span>
                          <span>{tab.label}</span>
                        </span>
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            )}
          </div>

          {/* Tab Content */}
          <div className={getNCBClasses.headerSection()}>
            {renderTabContent()}
          </div>

          {/* Footer Information */}
          {(footerTitle || footerStats) && (
            <div className={getNCBClasses.footerSection()}>
              <div className="text-center">
                {footerTitle && (
                  <h3 className={`text-2xl font-bold mb-4`}>{footerTitle}</h3>
                )}
                {footerDescription && (
                  <p
                    className={`${ncbInterpolColors.footer.subtext} leading-relaxed max-w-3xl mx-auto`}
                  >
                    {footerDescription}
                  </p>
                )}
                {footerStats && footerStats.length > 0 && (
                  <div className="mt-6 flex justify-center items-center gap-x-6">
                    {footerStats.map((stat, index) => (
                      <React.Fragment key={index}>
                        <div className="text-center">
                          <div className="text-2xl font-bold">{stat.value}</div>
                          <div
                            className={`text-sm ${ncbInterpolColors.footer.statText}`}
                          >
                            {stat.label}
                          </div>
                        </div>
                        {index < footerStats.length - 1 && (
                          <div
                            className={`w-px h-12 ${ncbInterpolColors.footer.divider}`}
                          ></div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePageTemplate;
